import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';
import { ProductModel } from '@app/products/shared/product.model';
import { CurrencyPipe } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-variant-pricing-label',
  template: `
    {{ variantName }}
    @if (selectedPrice > 0) {
      <span class="ml-2 text-green-600 dark:text-green-400 font-semibold">
        +{{ selectedPrice | currency:'GBP':'symbol':'1.2-2':'en-GB' }}
      </span>
    }
  `,
  imports: [CurrencyPipe],
  standalone: true,
})
export class VariantPricingLabelComponent implements OnInit, OnDestroy {
  @Input() variantId!: number;
  @Input() variantName!: string;
  @Input() product!: ProductModel;
  @Input() variantSelectionsFormArray!: FormArray;
  @Input() variantSelections?: any[]; // Event-specific variant selections with effective_price

  private destroy$ = new Subject<void>();

  selectedPrice: number = 0;

  ngOnInit(): void {
    // Subscribe to form changes to update the price display
    this.variantSelectionsFormArray.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.updatePrice());

    // Initial price calculation
    this.updatePrice();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private updatePrice(): void {
    // Find the form control for this variant
    const control = this.variantSelectionsFormArray.controls.find(ctrl =>
      ctrl.value.variant_id === this.variantId
    );

    if (!control?.value.variant_option_id) {
      this.selectedPrice = 0;
      return;
    }

    // First, try to get effective_price from event-specific variant selections
    if (this.variantSelections) {
      const eventSelection = this.variantSelections.find(
        vs => vs.variant_id === this.variantId && vs.variant_option_id === control.value.variant_option_id
      );
      if (eventSelection?.variant_option?.['effective_price'] !== undefined) {
        this.selectedPrice = eventSelection.variant_option['effective_price'];
        return;
      }
    }

    // Fallback to product variant options
    const variant = this.product?.variants?.find(v => v.id === this.variantId);
    const option = variant?.options?.find(o => o.id === control.value.variant_option_id);

    // Use effective_price if available (includes daily pricing), otherwise fall back to price
    this.selectedPrice = option?.['effective_price'] ?? option?.price ?? 0;
  }
}